<template>
  <v-autocomplete
    ref="suggestions"
    v-model="select"
    v-model:search="search"
    :items="items"
    density="compact"
    hide-no-data
    hide-details
    append-inner-icon=""
    class="suggestions"
    autocomplete="off"
    variant="plain"
    :placeholder="null"
    :label="null"
    :disabled="!editing"
    menu-icon=""
    data-testid="suggestions-autocomplete"
    @update:search="(searchTerm) => items = querySelections(searchTerm)"
    @update:model-value="updateSelection(select)"
    @update:menu="handleUpdateMenu"
  >
    <template #item="{props}">
      <v-list-item
        class="suggestion-item slim"
        v-bind="props"
      />
    </template>
  </v-autocomplete>
</template>

<script>
export default {
  name: 'FieldSuggestions',
  inject: {
    // An injected property that allows parent components to arbitrarily force a reevaluation of overflow.
    forceOverflowReevaluation: {
      default: false,
    },
  },
  props: {
    suggestions: {
      type: Array,
      default: () => [],
    },
    currentSelection: {
      type: String,
      default: () => '',
    },
    editing: {
      type: Boolean,
      default: false,
    },
    valueOverridden: {
      type: Boolean,
      default: false,
    },
    // TODO: fix styling to allow placeholder/label to be visible
    placeholder: {
      type: String,
      default: 'Search',
    },
    overflowing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-selection', 'update-overflowing'],
  data() {
    return {
      search: this.currentSelection,
      select: this.currentSelection + (this.valueOverridden ? ' *' : ''),
      items: this.querySelections(this.currentSelection),
    };
  },
  watch: {
    // Hacky fix as a result of fields nested within v-expansion-panel. The elements within have a 'scrollWidth' and
    // 'clientWidth' of 0, even when the 'eager' attribute is set to false, so the overflow evaluation logic always
    // returns false. The injected property 'forceOverflowReevaluation' can be toggled arbitrarily by parent components
    // to force a reevaluation of overflow.
    forceOverflowReevaluation(newVal) {
      if (newVal) {
        // We must wait for the next tick to make sure the 'scrollWidth' and 'clientWidth' properties are set prior to
        // checking overflow.
        this.$nextTick(() => {
          this.checkOverflow();
        });
      }
    },
    currentSelection(newVal) {
      if (newVal !== this.select) {
        this.search = newVal;
        this.select = newVal;
        this.items = this.querySelections(newVal);
      }
    },
    valueOverridden(newVal) {
      if (newVal) {
        this.select = this.select + ' *';
      } else {
        // Checking for the asterisk at the end should allow them to use asterisks in their field labels
        const indexOfAsterisk = this.select.lastIndexOf(' *');
        if (indexOfAsterisk > -1) {
          this.select = this.select.substring(0, indexOfAsterisk);
        }
      }
    },
  },
  mounted() {
    this.checkOverflow();
  },
  methods: {
    async handleUpdateMenu(open) {
      // if the menu closes while search does not match select, then select should be updated to match search
      if (this.search) {
        this.select = this.search;
      }
      if (!open) {
        this.updateSelection(this.select);
      }
      if (open) {
        this.items = this.querySelections(this.search || this.select);
      }
    },
    updateSelection(value) {
      this.$emit('update-selection', value);
    },
    querySelections(query) {
      let filtered = this.suggestions;
      query = (query || '').toLowerCase();
      if (query) {
        filtered = this.suggestions.filter((item) => (item || '').toLowerCase().indexOf(query) > -1);
      }
      return filtered;
    },
    isOverflown(element) {
      return element.scrollWidth > element.clientWidth;
    },
    checkOverflow() {
      const element = this.$refs.suggestions.$el;
      if (!element) return;
      const hoveredText = element.querySelector('.v-autocomplete__selection-text');
      if (!hoveredText) return;
      if (hoveredText && this.isOverflown(hoveredText)) {
        this.$emit('update-overflowing', true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.suggestion-item {
  &.slim {
    &.v-list-item {
      padding: 0 !important;
      margin: 0 !important;
      min-height: 0 !important;
    }
  }
}
:deep(.v-list-item__content) {
  max-height: 150px;
  width: auto;
  padding-top: 0px;
}
:deep(.v-list-item-title) {
  padding: 0 5px;
  font-size: 13px;
}

.suggestions {
  @extend .display-field-text;

  display: flex;
  flex-direction: column;
  :deep(.v-field) {
    padding: 0;
    height: 20px;
    &.v-field--disabled {
      opacity: unset;
      .v-autocomplete__selection-text {
        background-color: unset;
      }
    }

    .v-autocomplete__selection-text {
      font-size: 13px;
      background-color: main.$lightGray;
      height: 22px;
      border-radius: 2px;
      padding: 1px 5px;
      width: 195px;
      height: 21.5px;
      word-break: break-all;
      overflow: hidden;
    }
  }
  :deep(input) {
    font-size: 13px;
    background-color: main.$lightGray;
    height: 22px;
    border-radius: 2px;
    padding: 0px 5px !important;
    width: 100%;
    height: 21.5px;
  }
  :deep(.v-field__input) {
    display: contents;
  }
}
</style>
